import { Location } from "gen/company/models_pb"
import { UUID as PBUUID } from "gen/proto/uuid/models_pb"
import {
  BoolFilter,
  CustomPriceRuleValidityFilter,
  SpiffValidityFilter,
} from "gen/search/service/service_pb"
import { Tag } from "gen/tag/models_pb"
import { Vendor } from "gen/vendors/models_pb"
import { UUID } from "shared-ts/classes/uuid"
import { QueryParams, WebUrl } from "shared-web/lib/webUrl"

import {
  classFilterKey,
  departmentFilterKey,
  finelineFilterKey,
  vendorFilterKey,
} from "hooks/filters/filterKeys"
import { encodeQueryParam } from "hooks/filters/useWebFilter"

export interface ProductsFilters {
  vendor?: Pick<Vendor, "id" | "name">
  departmentId?: PBUUID
  classId?: PBUUID
  finelineId?: PBUUID
  productTag?: Pick<Tag, "id" | "name">
}

export const ProductTabs = {
  CATALOG: "catalog",
  HISTORY: "history",
  INVENTORY: "inventory",
  PRICING: "pricing",
  COSTS: "costs",
  SETTINGS: "settings",
} as const
export type ProductTab = (typeof ProductTabs)[keyof typeof ProductTabs]

export function product(
  id?: PBUUID,
  tab?: ProductTab,
  locationId?: PBUUID
): WebUrl {
  return {
    pathname: `/pos/products/${UUID.idString(id)}/${tab ?? ProductTabs.HISTORY}`,
    query: {
      ...(tab === ProductTabs.PRICING && {
        status: CustomPriceRuleValidityFilter.CPRVF_ACTIVE.toString(),
      }),
      ...(tab === ProductTabs.CATALOG &&
        locationId && {
          location: UUID.idString(locationId),
          discontinued: BoolFilter.FALSE.toString(),
        }),
    },
  }
}

export function products(filters?: ProductsFilters): WebUrl {
  const query: Record<string, string> = {
    status: BoolFilter.TRUE.toString(),
  }

  if (filters?.vendor?.id) {
    query[vendorFilterKey] = encodeQueryParam(
      { key: UUID.idString(filters.vendor.id), value: filters.vendor.name },
      true
    )
  }
  if (filters?.departmentId) {
    query[departmentFilterKey] = UUID.idString(filters.departmentId)
  }
  if (filters?.classId) {
    query[classFilterKey] = UUID.idString(filters.classId)
  }
  if (filters?.finelineId) {
    query[finelineFilterKey] = UUID.idString(filters.finelineId)
  }
  if (filters?.productTag?.id) {
    query.tag = encodeQueryParam(
      {
        key: UUID.idString(filters.productTag.id),
        value: filters.productTag.name,
      },
      true
    )
  }

  return {
    pathname: "/pos/products",
    query,
  }
}

export function productsWithTag(tag: Tag): WebUrl {
  return {
    pathname: "/pos/products",
    query: {
      tag: encodeQueryParam(
        { key: UUID.idString(tag.id), value: tag.name },
        true
      ),
    },
  }
}

export function customPrice(id?: PBUUID): WebUrl {
  return {
    pathname: `/pos/products/custom-prices/${UUID.idString(id)}`,
  }
}

export function newCustomPrice(opts?: {
  customerId?: PBUUID
  jobId?: PBUUID
}): WebUrl {
  const query: Record<string, string> = {}
  if (opts?.customerId) {
    query.customerId = UUID.idString(opts.customerId)
  }
  if (opts?.jobId) {
    query.jobId = UUID.idString(opts.jobId)
  }
  return {
    pathname: "/pos/products/custom-prices/new",
    query,
  }
}

export function customPrices(): WebUrl {
  return {
    pathname: "/pos/products/custom-prices",
    query: { status: CustomPriceRuleValidityFilter.CPRVF_ACTIVE.toString() },
  }
}

export function promotion(id?: PBUUID): WebUrl {
  return {
    pathname: `/pos/products/promotions/${UUID.idString(id)}`,
  }
}

export function newPromotion(): WebUrl {
  return {
    pathname: "/pos/products/promotions/new",
  }
}

export function promotions(): WebUrl {
  return {
    pathname: "/pos/products/promotions",
    query: { status: CustomPriceRuleValidityFilter.CPRVF_ACTIVE.toString() },
  }
}

interface PriceChangesOpts {
  orderId?: PBUUID
  orderShipmentId?: PBUUID
  vendor?: Pick<Vendor, "id" | "name">
}

export function priceChanges(opts?: PriceChangesOpts): WebUrl {
  const query: QueryParams = {}
  if (opts?.orderId) {
    query.orderID = UUID.idString(opts.orderId)
  }
  if (opts?.orderShipmentId) {
    query.orderShipmentID = UUID.idString(opts.orderShipmentId)
  }
  if (opts?.vendor?.id) {
    query[vendorFilterKey] = encodeQueryParam(
      { key: UUID.idString(opts.vendor.id), value: opts.vendor.name },
      true
    )
  }
  return {
    pathname: "/pos/products/integrations/price-changes",
    query,
  }
}

export function promotionalPriceChanges(): WebUrl {
  return {
    pathname: "/pos/products/integrations/promotions",
  }
}

export function itemMaintenance(): WebUrl {
  return {
    pathname: "/pos/products/integrations/item-maintenance",
  }
}

export function catalogSkus(location?: Pick<Location, "id" | "name">): WebUrl {
  const query = location && {
    location: encodeQueryParam(
      { key: UUID.idString(location.id), value: location.name },
      true
    ),
    discontinued: BoolFilter.FALSE.toString(),
  }
  return {
    pathname: "/pos/products/integrations/vendor-catalogs",
    query,
  }
}

export function catalogProducts(): WebUrl {
  return {
    pathname: "/pos/products/catalog",
  }
}

export const GiftCardsBasePath = "/pos/products/giftcards"
export function giftCards(): WebUrl {
  return { pathname: GiftCardsBasePath }
}

export function giftCard(id?: PBUUID): WebUrl {
  return {
    pathname: `${GiftCardsBasePath}/${UUID.idString(id)}`,
  }
}

export function spiffs(): WebUrl {
  return {
    pathname: "/pos/products/spiffs",
    query: { status: SpiffValidityFilter.ACTIVE.toString() },
  }
}
